import { useAnalysisSource } from '../components/financial/AnalysisSourceContext';
import { formatCurrency } from '../components/financial/CurrencyDisplay';
import { SENSITIVITY_VARIABLES, getForecastContext, formatQuarter } from '../types/financial-model';
import type { SensitivityConfig } from '../types/financial-model';
import clsx from 'clsx';

export function SensitivityView() {
  // Sourced through the context so the same view serves v1 and v2. With no
  // provider it reads the v1 store, which is what v1 does today.
  const {
    settings,
    sensitivity,
    isRecalculating,
    isDirty,
    updateSetting,
    recalculate: recalculateAndSave,
  } = useAnalysisSource();

  if (!settings || !sensitivity) return null;

  const config = settings.sensitivityConfig;
  const allScenarios = [sensitivity.baseCase, ...sensitivity.scenarios];
  const ctx = getForecastContext(settings);
  const lastQLabel = formatQuarter(ctx.lastQuarter);

  const metrics = [
    { key: 'totalRevenue', label: 'Total Revenue' },
    { key: 'totalCosts', label: 'Total Costs' },
    { key: 'grossProfit', label: 'Gross Profit' },
    { key: 'ebitda', label: 'EBITDA' },
    { key: 'profitAfterTax', label: 'Profit After Tax' },
    { key: 'closingCash', label: `Closing Cash (${lastQLabel})` },
    { key: 'netWorth', label: `Net Worth (${lastQLabel})` },
  ] as const;

  const updateConfig = (newConfig: SensitivityConfig) => {
    updateSetting('sensitivityConfig', newConfig);
  };

  const addScenario = () => {
    updateConfig({
      scenarios: [
        ...config.scenarios,
        { variable: 'revenue', shockPercent: 10 },
      ],
    });
  };

  const removeScenario = (index: number) => {
    updateConfig({
      scenarios: config.scenarios.filter((_, i) => i !== index),
    });
  };

  const updateScenarioVariable = (index: number, variable: string) => {
    const updated = config.scenarios.map((s, i) =>
      i === index ? { ...s, variable } : s,
    );
    updateConfig({ scenarios: updated });
  };

  const updateScenarioShock = (index: number, shockPercent: number) => {
    const updated = config.scenarios.map((s, i) =>
      i === index ? { ...s, shockPercent } : s,
    );
    updateConfig({ scenarios: updated });
  };

  return (
    <div className="p-6 overflow-auto h-full">
      <h2 className="text-lg font-bold text-gray-800 mb-4">Sensitivity Analysis</h2>
      <p className="text-sm text-gray-500 mb-6">
        Configure scenarios to analyze impact on key financial metrics. Choose variables and shock percentages below.
      </p>

      {/* Scenario Editor */}
      <div className="mb-6 bg-gray-50 rounded-lg p-4 border border-gray-200">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-gray-700">Scenarios</h3>
          <div className="flex items-center gap-2">
            <button
              onClick={addScenario}
              className="px-3 py-1.5 text-xs font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded-md hover:bg-blue-100 transition-colors"
            >
              + Add Scenario
            </button>
            <button
              onClick={() => void recalculateAndSave()}
              disabled={isRecalculating || !isDirty}
              className={clsx(
                'px-4 py-1.5 text-xs font-medium rounded-md transition-colors flex items-center gap-1.5',
                isDirty && !isRecalculating
                  ? 'text-white bg-green-600 hover:bg-green-700'
                  : 'text-gray-400 bg-gray-100 cursor-not-allowed',
              )}
            >
              {isRecalculating ? (
                <>
                  <svg className="w-3.5 h-3.5 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  Running...
                </>
              ) : (
                <>
                  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5.25 5.653c0-.856.917-1.398 1.667-.986l11.54 6.347a1.125 1.125 0 0 1 0 1.972l-11.54 6.347a1.125 1.125 0 0 1-1.667-.986V5.653Z" />
                  </svg>
                  Run
                </>
              )}
            </button>
          </div>
        </div>

        {config.scenarios.length === 0 && (
          <p className="text-sm text-gray-400 italic">No scenarios configured. Add one to begin.</p>
        )}

        <div className="space-y-2">
          {config.scenarios.map((scenario, index) => (
            <div key={index} className="flex items-center gap-3 bg-white rounded-md border border-gray-200 px-3 py-2">
              <select
                value={scenario.variable}
                onChange={(e) => updateScenarioVariable(index, e.target.value)}
                className="text-sm border border-gray-300 rounded px-2 py-1.5 bg-white focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
              >
                {SENSITIVITY_VARIABLES.map((v) => (
                  <option key={v.key} value={v.key}>
                    {v.label}
                  </option>
                ))}
              </select>

              <div className="flex items-center gap-1">
                <input
                  type="number"
                  value={scenario.shockPercent}
                  onChange={(e) => {
                    const val = parseFloat(e.target.value);
                    if (!isNaN(val)) updateScenarioShock(index, val);
                  }}
                  className="w-20 text-sm text-right border border-gray-300 rounded px-2 py-1.5 tabular-nums focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                />
                <span className="text-sm text-gray-500">%</span>
              </div>

              <button
                onClick={() => removeScenario(index)}
                className="ml-auto p-1 text-gray-400 hover:text-red-500 transition-colors"
                title="Remove scenario"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Results Table */}
      <div className={clsx('overflow-x-auto', isRecalculating && 'opacity-50 transition-opacity')}>
        <table className="border-collapse min-w-full">
          <thead>
            <tr>
              <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600 border-b-2 border-gray-300 w-48">
                Metric
              </th>
              {allScenarios.map((s, i) => (
                <th
                  key={i}
                  className={clsx(
                    'px-4 py-3 text-right text-sm font-semibold border-b-2 border-gray-300 min-w-[140px]',
                    s.label === 'Base Case' ? 'text-gray-800 bg-blue-50' : 'text-gray-600',
                  )}
                >
                  {s.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {metrics.map((metric) => (
              <tr key={metric.key} className="hover:bg-gray-50">
                <td className="px-4 py-3 text-sm text-gray-700 border-b border-gray-200 font-medium">
                  {metric.label}
                </td>
                {allScenarios.map((s, i) => {
                  const value = s[metric.key];
                  const baseValue = sensitivity.baseCase[metric.key];
                  const isBase = s.label === 'Base Case';
                  const diff = isBase ? 0 : value - baseValue;

                  return (
                    <td
                      key={i}
                      className={clsx(
                        'px-4 py-3 text-right text-sm tabular-nums border-b border-gray-200',
                        isBase ? 'bg-blue-50 font-semibold' : '',
                        value < 0 ? 'text-red-600' : 'text-gray-900',
                      )}
                    >
                      <div>{formatCurrency(value)}</div>
                      {!isBase && diff !== 0 && (
                        <div className={clsx('text-xs mt-0.5', diff > 0 ? 'text-green-600' : 'text-red-500')}>
                          {diff > 0 ? '+' : ''}{formatCurrency(diff)}
                        </div>
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
